export enum ClearedStatus {
  Cleared = 'cleared',
  NotAvailable = 'n/a',
  NotCleared = 'not cleared',
}

export const isClearedStatus = (value: unknown): value is ClearedStatus =>
  Object.values(ClearedStatus).includes(value as ClearedStatus);

export type ClearedStatusValue = boolean | null;

export const ClearedStatusData = [
  { label: 'Cleared', status: ClearedStatus.Cleared, value: true },
  { label: 'N/A', status: ClearedStatus.NotAvailable, value: null },
  { label: 'Not Cleared', status: ClearedStatus.NotCleared, value: false },
] as const satisfies {
  readonly label: string;
  readonly status: ClearedStatus;
  readonly value: ClearedStatusValue;
}[];

export type ClearedStatusModel<T extends ClearedStatus | ClearedStatusValue = ClearedStatus> =
  T extends ClearedStatus
    ? Extract<(typeof ClearedStatusData)[number], { status: T }>
    : T extends ClearedStatusValue
      ? Extract<(typeof ClearedStatusData)[number], { value: T }>
      : never;

export const getClearedStatusModel = <S extends ClearedStatus | ClearedStatusValue>(
  lookup: S,
): ClearedStatusModel<S> => {
  const models = ClearedStatusData.filter(model => {
    if (isClearedStatus(lookup)) {
      const status: ClearedStatus = lookup;
      return model.status === status;
    }
    return model.value === lookup;
  });
  if (models.length === 0) {
    throw new Error(`No cleared status model found for lookup: ${lookup}`);
  } else if (models.length > 1) {
    throw new Error(`Multiple cleared status models found for lookup: ${lookup}`);
  }
  return models[0] as ClearedStatusModel<S>;
};

/**
 * Given a cleared status, {@link ClearedStatus}, returns the corresponding value,
 * {@link ClearedStatusValue}.
 *
 * @param {ClearedStatus} status
 *   The cleared status for which the corresponding value should be returned.
 *
 * @returns {ClearedStatusModel<S>['value']}
 *   The cleared status value, {@link ClearedStatusValue}, associated with the given status,
 *   {@link ClearedStatus}.
 */
export const getClearedStatusValue = <S extends ClearedStatus>(
  status: S,
): ClearedStatusModel<S>['value'] => getClearedStatusModel(status).value;

/**
 * Given a cleared status, {@link ClearedStatus}, returns the corresponding label, {@link string}.
 *
 * @param {ClearedStatus} status
 *   The cleared status for which the corresponding label should be returned.
 *
 * @returns {ClearedStatusModel<S>['label']}
 *   The cleared status label, {@link string}, associated with the given status,
 *   {@link ClearedStatus}.
 */
export const getClearedStatusLabel = <S extends ClearedStatus>(
  status: S,
): ClearedStatusModel<S>['label'] => getClearedStatusModel(status).label;
